import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../config/api';

/**
 * MonthlyStatsWidget
 *
 * 4 KPI cards showing this-month totals with % delta vs last month.
 * Metrics: Messages Sent (real), Calls, Opens, Replies (stubbed until
 * phone + email integrations are wired).
 *
 * The endpoint returns `{ unavailable: true }` for stubbed metrics so
 * we can distinguish "actually zero" from "not yet tracked" — the
 * former shows "0" with a real delta, the latter shows "—" and
 * "Integration pending".
 */

const METRICS = [
  { key: 'messages_sent', label: 'Messages Sent' },
  { key: 'calls',         label: 'Calls' },
  { key: 'opens',         label: 'Opens' },
  { key: 'replies',       label: 'Replies' },
];

function formatDelta(current, prev) {
  if (prev === 0) {
    return current === 0
      ? { text: 'No change', kind: 'flat' }
      : { text: 'New this month', kind: 'up' };
  }
  const pct = ((current - prev) / prev) * 100;
  if (Math.abs(pct) < 1) return { text: '0%', kind: 'flat' };
  const rounded = Math.round(pct);
  return {
    text: `${rounded > 0 ? '+' : ''}${rounded}%`,
    kind: rounded > 0 ? 'up' : 'down',
  };
}

export default function MonthlyStatsWidget() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const token = localStorage.getItem('authToken');
        if (!token) return;
        const res = await fetch(`${API_BASE_URL}/api/dashboard/monthly-stats`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) {
          const body = await res.text().catch(() => '');
          console.error('[banyak-monthly-stats] fetch failed', { status: res.status, body: body.slice(0, 200) });
          return;
        }
        const json = await res.json();
        setStats(json.stats || null);
      } catch (e) {
        console.error('[banyak-monthly-stats] fetch threw', e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-1.5">
          <svg className="w-4 h-4 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
          This Month
        </h3>
        <span className="text-[10px] text-gray-500 uppercase tracking-wide font-semibold">
          vs Last Month
        </span>
      </div>

      {loading ? (
        <div className="py-6 text-center text-xs text-gray-400">Loading…</div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {METRICS.map(({ key, label }) => {
            const s = stats?.[key] || { this_month: 0, last_month: 0, unavailable: false };
            const unavailable = s.unavailable;
            const delta = unavailable ? null : formatDelta(s.this_month, s.last_month);
            return (
              <div key={key} className="min-w-0">
                <div className="text-[10px] text-gray-500 uppercase tracking-wide font-semibold mb-1 truncate">{label}</div>
                <div className={`text-2xl font-bold ${unavailable ? 'text-gray-300' : 'text-gray-900'} tabular-nums`}>
                  {unavailable ? '—' : s.this_month.toLocaleString()}
                </div>
                <div className={`text-[10px] font-medium mt-0.5 ${
                  unavailable ? 'text-gray-400' :
                  delta.kind === 'up'   ? 'text-emerald-600' :
                  delta.kind === 'down' ? 'text-red-600' :
                                          'text-gray-500'
                }`}>
                  {unavailable ? 'Integration pending' : delta.text}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
